<div class="flexi-compliance-page" *ngIf="!accessDenied; else denied">
  <header class="page-header">
    <button class="btn btn-link" type="button" (click)="backToList()">← Templates</button>
    <h2 *ngIf="template">{{ template.name }}</h2>
    <p class="subtitle" *ngIf="template">
      {{ template.catalogVersionCode }} · {{ template.catalogVersionName }}
    </p>
  </header>

  <div class="alert alert-danger" *ngIf="loadError">{{ loadError }}</div>
  <div *ngIf="loading" class="loading">Loading…</div>

  <!-- Copy-on-write notice: this business's edits forked the shared template -->
  <div class="alert alert-success" *ngIf="forkedNotice">
    {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.FORKED_NOTICE' | translate }}
    <button class="btn btn-sm btn-link" type="button" (click)="dismissForkedNotice()">
      {{ 'FLEXI_COMPLIANCE.DISMISS' | translate }}
    </button>
  </div>

  <!-- Shared-template warning: edits without a business context hit everyone -->
  <div class="alert alert-warning" *ngIf="template && isShared && !businessContextId">
    {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SHARED_WARNING'
       | translate: { count: template.businessAssignmentCount } }}
  </div>

  <ng-container *ngIf="template">
    <!-- ===== Metadata panel ===== -->
    <section class="card">
      <h3>{{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.FIELD_NAME' | translate }}</h3>
      <label>
        {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.FIELD_NAME' | translate }}
        <input type="text" [(ngModel)]="draftName" maxlength="200" />
      </label>
      <label>
        {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.FIELD_DESCRIPTION' | translate }}
        <textarea [(ngModel)]="draftDescription" rows="2" maxlength="1000"></textarea>
      </label>
      <label class="toggle">
        <input type="checkbox" [(ngModel)]="draftActive" />
        {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.FIELD_ACTIVE' | translate }}
      </label>
      <div class="form-actions">
        <button class="btn btn-primary" type="button"
                (click)="saveMeta()"
                [disabled]="savingMeta || !draftName.trim()">
          {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SAVE' | translate }}
        </button>
      </div>
    </section>

    <!-- ===== Items by section ===== -->
    <section class="card" *ngFor="let section of template.sections">
      <div class="section-header-row">
        <ng-container *ngIf="renameTopicId !== section.sectionId; else renameForm">
          <h3>
            {{ sectionLabel(section) }}
            <span class="badge badge-custom" *ngIf="section.isCustomTopic">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CUSTOM_TOPIC' | translate }}
            </span>
          </h3>
          <div class="row-actions">
            <button class="btn btn-sm btn-outline-secondary"
                    type="button"
                    *ngIf="section.isCustomTopic"
                    (click)="openRenameTopic(section.sectionId, section.title)">
              <i class="pi pi-pencil"></i>
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.RENAME_TOPIC' | translate }}
            </button>
            <button class="btn btn-sm btn-danger"
                    type="button"
                    (click)="removeSection(section.sectionId)"
                    [disabled]="busySectionIds.has(section.sectionId)">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.REMOVE_SECTION' | translate }}
            </button>
          </div>
        </ng-container>
        <ng-template #renameForm>
          <div class="inline-form">
            <input type="text" [(ngModel)]="renameTopicTitle" maxlength="300" />
            <button class="btn btn-sm btn-primary" type="button"
                    (click)="submitRenameTopic()"
                    [disabled]="renamingTopic || !renameTopicTitle.trim()">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SAVE' | translate }}
            </button>
            <button class="btn btn-sm btn-link" type="button" (click)="cancelRenameTopic()">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CANCEL' | translate }}
            </button>
          </div>
        </ng-template>
      </div>
      <p class="muted">{{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SECTION_HEADER' | translate }}</p>

      <ul class="item-list" *ngIf="section.items.length > 0; else emptySection">
        <li *ngFor="let item of section.items">
          <span class="badge" [class.badge-custom]="item.sourceType === 1">
            {{ item.sourceType === 1 ? 'Custom' : 'Catalog' }}
          </span>
          <div class="item-body">
            <div class="question">{{ item.question }}</div>
            <div class="muted" *ngIf="item.regulatoryReference">{{ item.regulatoryReference }}</div>
            <div class="muted">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ITEM_WEIGHT' | translate }}: {{ item.weight }}
            </div>
          </div>
          <button class="btn btn-sm btn-danger"
                  type="button"
                  (click)="removeItem(item.id)"
                  [disabled]="busyItemIds.has(item.id)">
            {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.REMOVE_ITEM' | translate }}
          </button>
        </li>
      </ul>
      <ng-template #emptySection>
        <p class="muted">No items in this section yet.</p>
      </ng-template>

      <div class="section-add-row">
        <!-- Add a standard (catalog) question. This used to be a bare numeric
             input asking for a "Catalog item ID" — a database key, so nobody
             outside the dev team could use it. It now picks from the version's
             own catalog by question text, minus whatever the template already
             holds. -->
        <button class="btn btn-link"
                type="button"
                *ngIf="!addCatalogOpen.has(section.sectionId)"
                [disabled]="catalogExhausted(section)"
                [title]="catalogExhausted(section)
                  ? ('FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CATALOG_ALL_ADDED' | translate)
                  : ''"
                (click)="openAddCatalog(section.sectionId)">
          + {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ADD_FROM_CATALOG' | translate }}
        </button>
        <div class="inline-form inline-form-catalog" *ngIf="addCatalogOpen.has(section.sectionId)">
          <span class="inline-form__hint" *ngIf="catalogLoading">
            {{ 'FLEXI_COMPLIANCE.LOADING' | translate }}
          </span>
          <span class="inline-form__hint" *ngIf="!catalogLoading && availableCatalogItems(section).length === 0">
            {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CATALOG_ALL_ADDED' | translate }}
          </span>
          <ng-container *ngIf="!catalogLoading && availableCatalogItems(section).length > 0">
            <select class="inline-form__select"
                    [(ngModel)]="addCatalogInput[section.sectionId].catalogItemId"
                    (ngModelChange)="onCatalogItemPicked(section)">
              <option [ngValue]="null" disabled>
                {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CATALOG_PICK' | translate }}
              </option>
              <option *ngFor="let ci of availableCatalogItems(section)" [ngValue]="ci.id">
                {{ ci.question }}
              </option>
            </select>
            <input type="number" step="0.01" min="0.01"
                   [attr.aria-label]="'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ITEM_WEIGHT' | translate"
                   [placeholder]="'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ITEM_WEIGHT' | translate"
                   [(ngModel)]="addCatalogInput[section.sectionId].weight" />
            <button class="btn btn-sm btn-primary" type="button"
                    [disabled]="!addCatalogInput[section.sectionId].catalogItemId"
                    (click)="submitAddCatalog(section.sectionId)">
              {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SAVE' | translate }}
            </button>
          </ng-container>
          <button class="btn btn-sm btn-link" type="button" (click)="cancelAddCatalog(section.sectionId)">
            {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CANCEL' | translate }}
          </button>
        </div>

        <!-- Add custom item -->
        <button class="btn btn-link"
                type="button"
                *ngIf="!addCustomOpen.has(section.sectionId)"
                (click)="openAddCustom(section.sectionId)">
          + {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ADD_CUSTOM' | translate }}
        </button>
        <div class="inline-form inline-form-custom" *ngIf="addCustomOpen.has(section.sectionId)">
          <textarea rows="2"
                    placeholder="Question"
                    [(ngModel)]="addCustomInput[section.sectionId].question"></textarea>
          <input type="text"
                 placeholder="Regulatory reference (optional)"
                 [(ngModel)]="addCustomInput[section.sectionId].ref" />
          <input type="number" step="0.01" min="0.01"
                 placeholder="Weight"
                 [(ngModel)]="addCustomInput[section.sectionId].weight" />
          <button class="btn btn-sm btn-primary" type="button" (click)="submitAddCustom(section.sectionId)">
            {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SAVE' | translate }}
          </button>
          <button class="btn btn-sm btn-link" type="button" (click)="cancelAddCustom(section.sectionId)">
            {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CANCEL' | translate }}
          </button>
        </div>
      </div>
    </section>

    <!-- ===== Add custom topic ===== -->
    <section class="card">
      <button class="btn btn-link" type="button"
              *ngIf="!addTopicOpen"
              (click)="openAddTopic()">
        + {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.ADD_TOPIC' | translate }}
      </button>
      <div class="inline-form" *ngIf="addTopicOpen">
        <input type="text" [(ngModel)]="addTopicTitle" maxlength="300"
               [placeholder]="'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.TOPIC_TITLE_PLACEHOLDER' | translate" />
        <button class="btn btn-sm btn-primary" type="button"
                (click)="submitAddTopic()"
                [disabled]="addingTopic || !addTopicTitle.trim()">
          {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.SAVE' | translate }}
        </button>
        <button class="btn btn-sm btn-link" type="button" (click)="cancelAddTopic()">
          {{ 'FLEXI_COMPLIANCE_TEMPLATE.EDITOR.CANCEL' | translate }}
        </button>
      </div>
    </section>

    <!-- ===== Business assignments ===== -->
    <section class="card">
      <h3>{{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.TITLE' | translate }}</h3>
      <div class="alert alert-danger" *ngIf="assignmentsError">{{ assignmentsError }}</div>

      <div class="inline-form">
        <input type="number"
               placeholder="Business ID"
               [(ngModel)]="newAssignBusinessId" />
        <label class="toggle">
          <input type="checkbox" [(ngModel)]="newAssignDefault" />
          {{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.MARK_DEFAULT' | translate }}
        </label>
        <button class="btn btn-primary" type="button"
                (click)="submitAssign()"
                [disabled]="assigning || !newAssignBusinessId">
          {{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.ASSIGN_BUTTON' | translate }}
        </button>
      </div>

      <table class="data-table" *ngIf="assignments.length > 0; else noAssignments">
        <thead>
          <tr>
            <th>Business ID</th>
            <th>{{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.COLUMN_DEFAULT' | translate }}</th>
            <th>{{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.COLUMN_ASSIGNED' | translate }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of assignments">
            <td>{{ row.businessId }}</td>
            <td>{{ row.isDefault ? '✓' : '' }}</td>
            <td>{{ row.assignedOn | date:'dd-MM-yyyy' }}</td>
            <td>
              <button class="btn btn-sm btn-danger"
                      type="button"
                      (click)="unassign(row)"
                      [disabled]="busyAssignmentIds.has(row.id)">
                {{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.UNASSIGN' | translate }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <ng-template #noAssignments>
        <p class="muted">{{ 'FLEXI_COMPLIANCE_TEMPLATE.ASSIGN.EMPTY' | translate }}</p>
      </ng-template>
    </section>
  </ng-container>
</div>

<ng-template #denied>
  <div class="flexi-compliance-page">
    <p class="empty">{{ 'FLEXI_COMPLIANCE_TEMPLATE.ACCESS_DENIED' | translate }}</p>
  </div>
</ng-template>
